import { createApp } from "vue";
import VueCookies from "vue-cookies";
import App from "./App.vue";
import { i18n } from "./i18n";
import vuetify from "./plugins/vuetify";
import router from "./router";
import store from "./store";
import "@earthbrain/foundation-excavation-sdk/components";
import "./assets/style_custom.css";
import "@/assets/icons/style.css";
import axios from "axios";
import { getCookie } from "./utils/getCookie";

if (import.meta.env.DEV) {
	// ローカル実行時にはクロスオリジンの場合でもcookie情報を送れるようにする。同一オリジンでの場合は自動的にcookieが付与される
	axios.defaults.withCredentials = true;

	// HMR (Hot Module Replacement)
	if (import.meta.hot) {
		import.meta.hot.accept(["./store"], (newModule) => {
			// Preserve store state when component is updated
			if (newModule?.[0]) {
				console.log("Store module reloaded");
			}
		});
	}
}

const accessToken = getCookie("access_token");

// main.ts can finish evaluating after DOMContentLoaded (always in dev, where
// modules load one by one), so a plain listener would never register the SW.
if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => {
		registerServiceWorker();
	});
} else {
	registerServiceWorker();
}

async function registerServiceWorker() {
	if (!("serviceWorker" in navigator) || !accessToken) return;

	try {
		// 1. 登録 - 開発環境と本番環境で異なるパス
		const swBasePath = import.meta.env.DEV
			? "/src/workers/ebPlatformProxy.js"
			: EB_PLATFORM_PROXY_SW_PATH;

		// The SW runs outside the page, so it cannot read self.__ENV__ from
		// /env.js. Pass the landlog API base via the registration URL; the SW
		// falls back to its build-time value when the param is absent. A changed
		// value changes the registration URL, which the browser treats as a new
		// SW (updatefound -> SKIP_WAITING -> reload below), so the active worker
		// never keeps a stale value past the next page load.
		const landlogApiUrl: string | undefined = import.meta.env
			.VITE_LANDLOG_API_URL;
		const swPath = landlogApiUrl
			? `${swBasePath}?landlogApiUrl=${encodeURIComponent(landlogApiUrl)}`
			: swBasePath;

		const registration = await navigator.serviceWorker.register(swPath, {
			// 開発時は /src/workers/ 配下から配信されるため、明示しないとページを制御できない
			scope: "/",
			type: "module", // 常にmoduleタイプを使用
			updateViaCache: "none", // キャッシュを使わず常に最新を取得
		});
		console.log("ServiceWorker registered:", registration, "at path:", swPath);

		// 更新チェック
		registration.update();

		// 2. ready が解決されるのを待つ（アクティブ状態になったら resolve）
		const readyReg = await navigator.serviceWorker.ready;

		// 3. アクティブなワーカーにメッセージをポスト
		readyReg.active?.postMessage({ accessToken });

		// 更新があった場合の処理
		registration.addEventListener("updatefound", () => {
			const newWorker = registration.installing;
			console.log("New service worker available");
			newWorker?.addEventListener("statechange", () => {
				if (
					newWorker.state === "installed" &&
					navigator.serviceWorker.controller
				) {
					console.log("New service worker installed, reloading...");
					// 新しいワーカーをアクティブにする
					newWorker.postMessage({ type: "SKIP_WAITING" });
				}
			});
		});

		// controllerchange イベントで自動リロード
		navigator.serviceWorker.addEventListener("controllerchange", () => {
			console.log("Service worker updated, reloading page...");
			window.location.reload();
		});
	} catch (error) {
		console.error("ServiceWorker registration failed:", error);
	}
}

const app = createApp(App);
app.use(store);
app.use(router);
app.use(vuetify);
app.use(i18n);
app.use(VueCookies);

app.mount("#app");
